// F-066 : agenda transversal, vue jour / semaine, tâches et bloc « À faire aujourd'hui » du tableau de bord.
import { useState } from "react";
import { Link } from "react-router";
import { del, patch, post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateLongFmt, dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Empty, ErrorBox, Input, Loading, PageHeader, Select, Stat, Tabs, Textarea, fieldError } from "@core/ui";
import { DEPARTURE_STATUS, DUE_KINDS, OverdueBadge, PRIORITIES, PropertySelect, Status, TASK_STATUS, addDays, options, orgApi, orgPath, todayIso, useAction } from "./shared";

type Item = {
  key: string;
  kind: string;
  title: string;
  detail: string;
  date: string | null;
  at: string | null;
  due_kind: string;
  priority: string;
  overdue: boolean;
  property_label: string | null;
  origin_type: string;
  origin_id: string;
  link: string;
};

function AgendaItemRow({ orgId, item, onDone }: { orgId: string; item: Item; onDone?: () => void }) {
  const act = useAction();
  return (
    <div className={`vie-agenda-item ${item.overdue ? "vie-agenda-item-late" : ""}`}>
      <div className="stack-sm">
        <p className="row">
          {item.overdue ? <OverdueBadge /> : null}
          <Badge tone={item.due_kind === "echeance" ? "encre" : "neutre"}>{DUE_KINDS[item.due_kind]}</Badge>
          {item.priority === "haute" ? <Badge tone="attention">Priorité haute</Badge> : null}
          <strong>{item.title}</strong>
        </p>
        <p className="small muted">
          {item.at ? dateTime(item.at) : item.date ? date(item.date) : "Sans date"}
          {item.property_label ? ` · ${item.property_label}` : ""}
          {item.detail ? ` · ${item.detail}` : ""}
        </p>
      </div>
      <div className="row">
        {item.origin_type === "task" && onDone ? (
          <Button
            small
            variant="secondaire"
            busy={act.busy}
            onClick={async () => {
              if (await act.run(() => patch(orgApi(orgId, `/tasks/${item.origin_id}`), { status: "fait" }), "Tâche terminée.")) onDone();
            }}
          >
            Marquer fait
          </Button>
        ) : null}
        <Link className="bv-btn bv-btn-discret bv-btn-petit" to={orgPath(orgId, item.link)}>
          Ouvrir
        </Link>
      </div>
    </div>
  );
}

/** Lundi de la semaine d'une date ISO. */
function monday(iso: string) {
  const d = new Date(`${iso}T12:00:00`);
  const day = (d.getDay() + 6) % 7;
  return addDays(iso, -day);
}

export function AgendaPage() {
  const org = useOrg();
  const [view, setView] = useState<"jour" | "semaine">("jour");
  const [day, setDay] = useState(todayIso());
  const [propertyId, setPropertyId] = useState("");
  const from = view === "jour" ? day : monday(day);
  const to = view === "jour" ? day : addDays(from, 6);
  const path = orgApi(org.orgId, `/agenda?from=${from}&to=${to}${propertyId ? `&property_id=${propertyId}` : ""}`);
  const q = useGet<{ today: string; items: Item[]; counts: any }>(path);
  const invalidate = useInvalidate();
  const refresh = () => invalidate(orgApi(org.orgId, "/agenda"), orgApi(org.orgId, "/tasks"), orgApi(org.orgId, "/summary"));
  const items = q.data?.items ?? [];
  const overdue = items.filter((i) => i.overdue);
  const dated = items.filter((i) => !i.overdue && i.date);
  const undated = items.filter((i) => !i.overdue && !i.date);
  const days = view === "jour" ? [day] : Array.from({ length: 7 }, (_, n) => addDays(from, n));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Vie du logement"
        title="Agenda"
        intro="Loyers à encaisser, fins de bail, interventions, entretiens, départs et tâches. Une échéance est une date légale ou contractuelle ; reporter un rappel ne la déplace pas."
      />
      <div className="row-between">
        <Tabs value={view} onChange={setView} items={[{ value: "jour", label: "Jour" }, { value: "semaine", label: "Semaine" }]} />
        <div className="row">
          <Button small variant="secondaire" onClick={() => setDay(addDays(day, view === "jour" ? -1 : -7))}>
            Précédent
          </Button>
          <Button small variant="secondaire" onClick={() => setDay(todayIso())}>
            Aujourd’hui
          </Button>
          <Button small variant="secondaire" onClick={() => setDay(addDays(day, view === "jour" ? 1 : 7))}>
            Suivant
          </Button>
        </div>
      </div>
      <PropertySelect orgId={org.orgId} value={propertyId} onChange={setPropertyId} label="Filtrer par logement" />
      <h2>{view === "jour" ? dateLongFmt(day) : `Semaine du ${dateLongFmt(from)} au ${dateLongFmt(to)}`}</h2>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : (
        <>
          <div className="stats">
            <Stat label="En retard" value={q.data?.counts.overdue ?? 0} />
            <Stat label="Aujourd’hui" value={q.data?.counts.today ?? 0} />
            <Stat label="À venir sur la période" value={q.data?.counts.upcoming ?? 0} />
            <Stat label="À suivre sans date" value={q.data?.counts.to_follow ?? 0} />
          </div>
          {overdue.length ? (
            <Card title="En retard">
              <div className="vie-agenda-day">
                {overdue.map((i) => (
                  <AgendaItemRow key={i.key} orgId={org.orgId} item={i} onDone={refresh} />
                ))}
              </div>
            </Card>
          ) : null}
          {days.map((d) => {
            const list = dated.filter((i) => i.date === d);
            return (
              <Card key={d} title={view === "jour" ? (d === q.data?.today ? "Aujourd’hui" : "Ce jour-là") : dateLongFmt(d)}>
                {list.length ? (
                  <div className="vie-agenda-day">
                    {list.map((i) => (
                      <AgendaItemRow key={i.key} orgId={org.orgId} item={i} onDone={refresh} />
                    ))}
                  </div>
                ) : (
                  <p className="muted small">Rien de prévu.</p>
                )}
              </Card>
            );
          })}
          {undated.length ? (
            <Card title="À suivre">
              <div className="vie-agenda-day">
                {undated.map((i) => (
                  <AgendaItemRow key={i.key} orgId={org.orgId} item={i} onDone={refresh} />
                ))}
              </div>
            </Card>
          ) : null}
        </>
      )}
      <TasksCard orgId={org.orgId} onChange={refresh} />
    </div>
  );
}

function TasksCard({ orgId, onChange }: { orgId: string; onChange: () => void }) {
  const org = useOrg();
  const [showDone, setShowDone] = useState(false);
  const path = orgApi(orgId, `/tasks${showDone ? "?include_done=true" : ""}`);
  const q = useGet<{ items: any[] }>(path);
  const members = useGet<{ items: any[] }>(`/api/v1/orgs/${orgId}/members`);
  const [form, setForm] = useState({ title: "", due_on: "", priority: "normale", notes: "", assignee_user_id: "" });
  const [postpone, setPostpone] = useState<{ id: string; due_on: string } | null>(null);
  const act = useAction();
  const memberOptions = [{ value: "", label: "Personne" }, ...(members.data?.items ?? []).map((m: any) => ({ value: m.user_id, label: m.full_name }))];
  const refresh = () => {
    onChange();
  };
  return (
    <Card title="Tâches" actions={<Button small variant="discret" onClick={() => setShowDone(!showDone)}>{showDone ? "Masquer les tâches terminées" : "Afficher les tâches terminées"}</Button>}>
      <ErrorBox error={act.error} />
      {org.can("operate") ? (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await act.run(() => post(orgApi(orgId, "/tasks"), { ...form, due_on: form.due_on || undefined, assignee_user_id: form.assignee_user_id || undefined }), "Tâche ajoutée.");
            if (r) {
              setForm({ title: "", due_on: "", priority: "normale", notes: "", assignee_user_id: "" });
              refresh();
            }
          }}
        >
          <div className="form-row">
            <Input label="Nouvelle tâche" required value={form.title} error={fieldError(act.error, "title")} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Input label="Rappel le" type="date" value={form.due_on} onChange={(e) => setForm({ ...form, due_on: e.target.value })} />
            <Select label="Priorité" value={form.priority} options={options(PRIORITIES)} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
            <Select label="Responsable" value={form.assignee_user_id} options={memberOptions} onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })} />
          </div>
          <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <div>
            <Button type="submit" variant="secondaire" busy={act.busy}>
              Ajouter
            </Button>
          </div>
        </form>
      ) : null}
      {q.isLoading ? (
        <Loading />
      ) : q.data?.items.length ? (
        <ul className="list-plain">
          {q.data.items.map((t) => (
            <li key={t.id} className="vie-agenda-item">
              <div>
                <p className="row">
                  {t.overdue ? <OverdueBadge /> : null}
                  <Status value={t.status} labels={TASK_STATUS} />
                  <strong>{t.title}</strong>
                </p>
                <p className="small muted">
                  {t.due_on ? `Rappel le ${date(t.due_on)}` : "Sans date"} · priorité {PRIORITIES[t.priority].toLowerCase()}
                  {t.assignee_name ? ` · ${t.assignee_name}` : ""}
                  {t.notes ? ` · ${t.notes}` : ""}
                </p>
              </div>
              {org.can("operate") && ["a_faire", "en_cours"].includes(t.status) ? (
                <div className="row">
                  <Button small onClick={async () => { if (await act.run(() => patch(orgApi(orgId, `/tasks/${t.id}`), { status: "fait" }), "Tâche terminée.")) refresh(); }}>
                    Fait
                  </Button>
                  <Button small variant="secondaire" onClick={() => setPostpone({ id: t.id, due_on: t.due_on ?? todayIso() })}>
                    Reporter
                  </Button>
                  <Button small variant="discret" onClick={async () => { if (await act.run(() => del(orgApi(orgId, `/tasks/${t.id}`)), "Tâche annulée.")) refresh(); }}>
                    Annuler
                  </Button>
                  <Link className="bv-btn bv-btn-discret bv-btn-petit" to={orgPath(orgId, t.link)}>
                    Ouvrir
                  </Link>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <Empty title="Aucune tâche en cours" />
      )}
      {postpone ? (
        <form
          className="row"
          onSubmit={async (e) => {
            e.preventDefault();
            if (await act.run(() => patch(orgApi(orgId, `/tasks/${postpone.id}`), { due_on: postpone.due_on }), "Rappel reporté. Les échéances légales ne changent pas.")) {
              setPostpone(null);
              refresh();
            }
          }}
        >
          <Input label="Nouveau rappel le" type="date" value={postpone.due_on} onChange={(e) => setPostpone({ ...postpone, due_on: e.target.value })} />
          <Button type="submit" small>
            Reporter
          </Button>
          <Button small variant="discret" onClick={() => setPostpone(null)}>
            Fermer
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

/** Bloc du tableau de bord : à faire aujourd'hui, incidents ouverts, départs en cours. */
export function VieDashboard({ orgId }: { orgId: string }) {
  const q = useGet<any>(orgApi(orgId, "/summary"));
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return null;
  const d = q.data;
  return (
    <Card title="Vie du logement" actions={<Link to={orgPath(orgId, "agenda")}>Agenda</Link>}>
      <div className="stats">
        <Stat label="En retard" value={d.counts.overdue} />
        <Stat label="Incidents ouverts" value={d.incidents_open} hint={d.incidents_urgent ? `dont ${d.incidents_urgent} urgent${d.incidents_urgent > 1 ? "s" : ""}` : undefined} />
        <Stat label="Départs en cours" value={d.departures.length} />
        <Stat label="Messages non lus" value={d.unread_messages} />
      </div>
      <h3>À faire aujourd’hui</h3>
      {d.urgent.length ? (
        <div className="vie-agenda-day">
          {d.urgent.map((i: Item) => (
            <AgendaItemRow key={i.key} orgId={orgId} item={i} />
          ))}
        </div>
      ) : (
        <p className="muted">Rien d’urgent aujourd’hui.</p>
      )}
      {d.departures.length ? (
        <>
          <h3>Départs en cours</h3>
          <ul className="list-plain">
            {d.departures.map((x: any) => (
              <li key={x.id}>
                <Link to={orgPath(orgId, `departs/${x.id}`)}>{x.property_label}</Link> <Status value={x.status} labels={DEPARTURE_STATUS} />{" "}
                <span className="small muted">{x.end_on ? `fin ${x.confirmed ? "confirmée" : "proposée"} le ${date(x.end_on)}` : ""}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}
